import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABEL } from "@/lib/api";

export default function Portfolio() {
  const [s, setS] = useState(null);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    (async () => {
      const [sum, list] = await Promise.all([api.get("/portfolio/summary"), api.get("/requests")]);
      setS(sum.data);
      setRequests(list.data.requests || []);
    })();
  }, []);

  if (!s) return <div className="text-sm text-[hsl(var(--bc-muted))]">Loading…</div>;

  const total = s.total_requests || 1;
  return (
    <div data-testid="portfolio-page">
      <section className="bc-rise">
        <p className="eyebrow">Portfolio</p>
        <h1 className="font-display text-[44px] tracking-tight leading-[1.05] mt-3">Delivery governance, at a glance.</h1>
        <p className="mt-4 max-w-2xl text-[15px] text-[hsl(var(--bc-ink-soft))]">
          Delivery-level signal only. No individual productivity scoring, no lines-of-code ranking,
          no developer surveillance — by design.
        </p>
      </section>

      <section className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 bc-rise bc-rise-d1" data-testid="portfolio-counters">
        <Counter k="Total requests" v={s.total_requests} />
        <Counter k="Receipts signed" v={s.receipts_signed} />
        <Counter k="Governed actions" v={s.governed_actions_recorded} />
        <Counter k="Release ready" v={s.status_buckets?.release_ready || 0} />
      </section>

      <section className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="bc-card p-7 lg:col-span-7" data-testid="status-buckets">
          <p className="eyebrow mb-4">Status mix</p>
          <div className="space-y-3">
            {Object.entries(s.status_buckets).map(([k, v]) => {
              const pct = Math.round((v / total) * 100);
              return (
                <div key={k}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span>{STATUS_LABEL[k] || k}</span>
                    <span className="font-mono text-xs text-[hsl(var(--bc-muted))]">{v} · {pct}%</span>
                  </div>
                  <div className="h-[6px] bg-[hsl(var(--bc-line-soft))] rounded mt-2 overflow-hidden">
                    <div className="h-full bg-[hsl(var(--bc-signal))]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bc-card p-7 lg:col-span-5" data-testid="dependency-hotspots">
          <p className="eyebrow mb-4">Dependency hotspots</p>
          <ul className="space-y-3">
            {s.dependency_hotspots.map((d, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                <span className="text-sm">{d.dependency}</span>
                <span className="font-mono text-xs text-[hsl(var(--bc-muted))]">×{d.count}</span>
              </li>
            ))}
          </ul>
          <div className="rule my-6" />
          <p className="eyebrow mb-3">Budget band mix</p>
          <ul className="space-y-2 text-sm">
            {Object.entries(s.budget_bands).map(([k, v]) => (
              <li key={k} className="flex justify-between"><span>{k}</span><span className="font-mono text-xs text-[hsl(var(--bc-muted))]">×{v}</span></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-12" data-testid="portfolio-coverage">
        <div className="bc-card-flat p-7">
          <p className="eyebrow mb-3">KATLAS receipt coverage</p>
          <p className="font-display text-[26px] leading-snug">{s.katlas_receipt_coverage}</p>
        </div>
      </section>

      <section className="mt-12">
        <p className="eyebrow mb-4">All requests</p>
        <div className="bc-card-flat divide-y divide-[hsl(var(--border))]">
          {requests.map((r) => (
            <Link key={r.id} to={`/request/${r.request_id}`} className="p-4 flex items-center justify-between hover:bg-[hsl(var(--bc-paper))]">
              <span className="font-mono text-sm">{r.request_id}</span>
              <span className="text-sm flex-1 px-4 truncate">{r.requested_module}</span>
              <span className="text-xs text-[hsl(var(--bc-muted))]">{STATUS_LABEL[r.status]}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function Counter({ k, v }) {
  return (
    <div className="bc-card-flat p-6">
      <p className="eyebrow">{k}</p>
      <p className="bc-numeral mt-3">{v}</p>
    </div>
  );
}
